import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { EventInstance } from '@pacer/shared';
import * as api from '../api';
import { EventEditor } from './EventEditor';
import { Icon } from '../components/Icon';
import { Badge, Button, IconButton, cx } from '../components/ui';
import { daysSpanned, fmtRange, monthGrid, useZone } from '../lib/dates';
import { eventCategory, eventColor } from '../lib/events';
import { queryKeys } from '../state/queries';
import { useActiveMemberId } from '../state/activeMember';
import { type IconName } from '../components/Icon';

export function Calendar() {
  const zone = useZone();
  const [cursor, setCursor] = useState(() => DateTime.now().setZone(zone).startOf('month'));
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ instance?: EventInstance; day?: string } | null>(null);
  const [filterMine, setFilterMine] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const activeId = useActiveMemberId();

  // The grid bleeds into neighbouring months, so fetch the visible span, not the month.
  const grid = useMemo(() => monthGrid(cursor.setZone(zone)), [cursor, zone]);
  const from = grid[0]!.startOf('day').toUTC().toISO()!;
  const to = grid[41]!.endOf('day').toUTC().toISO()!;

  const memberFilter = filterMine ? activeId : null;
  const { data: allEvents = [] } = useQuery({
    queryKey: queryKeys.events(from, to, memberFilter),
    queryFn: () => api.getEvents(from, to, memberFilter),
  });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const { data: categories = [] } = useQuery({ queryKey: queryKeys.categories, queryFn: api.getCategories });

  // Category is filtered client-side so switching it doesn't re-hit the server.
  const events = useMemo(
    () => (filterCategory ? allEvents.filter((e) => e.categoryId === filterCategory) : allEvents),
    [allEvents, filterCategory],
  );

  const colorOf = (ev: EventInstance): string => eventColor(ev, members, categories);
  const iconOf = (ev: EventInstance): IconName | null =>
    (eventCategory(ev, categories)?.icon as IconName | undefined) ?? null;

  const byDay = useMemo(() => {
    const map = new Map<string, EventInstance[]>();
    for (const ev of events) {
      for (const day of daysSpanned(ev, zone)) {
        map.set(day, [...(map.get(day) ?? []), ev]);
      }
    }
    return map;
  }, [events, zone]);

  const todayIso = DateTime.now().setZone(zone).toISODate();
  const selectedEvents = selected ? (byDay.get(selected) ?? []) : [];

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b border-border-subtle px-4 py-3 md:px-6">
        <div className="min-w-0">
          <span className="eyebrow">{cursor.toFormat('yyyy')}</span>
          <h1 className="font-display text-[22px] leading-tight text-primary">
            {cursor.toFormat('LLLL')}
          </h1>
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          {activeId && (
            <Button
              variant={filterMine ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setFilterMine((v) => !v)}
            >
              Mine
            </Button>
          )}
          <IconButton
            icon="chevron-left"
            label="Previous month"
            onClick={() => setCursor((c) => c.minus({ months: 1 }))}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCursor(DateTime.now().setZone(zone).startOf('month'))}
          >
            Today
          </Button>
          <IconButton
            icon="chevron-right"
            label="Next month"
            onClick={() => setCursor((c) => c.plus({ months: 1 }))}
          />
          <Button
            variant="primary"
            icon="plus"
            onClick={() => setEditing({ day: selected ?? todayIso ?? undefined })}
          >
            Event
          </Button>
        </div>
      </header>

      {categories.length > 0 && (
        <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-border-subtle px-4 py-2 md:px-6">
          <button
            onClick={() => setFilterCategory(null)}
            className={cx(
              'shrink-0 rounded-full border px-3 py-1 text-[12px] transition-colors',
              filterCategory === null ? 'border-border text-primary' : 'border-border-subtle text-muted',
            )}
          >
            All
          </button>
          {categories.map((cat) => {
            const on = filterCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setFilterCategory(on ? null : cat.id)}
                className="flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] transition-colors"
                style={{
                  borderColor: on ? cat.color : 'var(--color-border-subtle)',
                  background: on ? `color-mix(in srgb, ${cat.color} 14%, transparent)` : 'transparent',
                  color: on ? cat.color : 'var(--color-text-muted)',
                }}
              >
                <span className="size-2 rounded-full" style={{ background: cat.color }} />
                {cat.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid shrink-0 grid-cols-7 border-b border-border-subtle">
        {grid.slice(0, 7).map((d) => (
          <div key={d.weekday} className="eyebrow py-2 text-center">
            {d.toFormat('ccc')}
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
        {grid.map((day) => {
          const iso = day.toISODate()!;
          const dayEvents = byDay.get(iso) ?? [];
          const outside = day.month !== cursor.month;
          const isToday = iso === todayIso;

          return (
            <button
              key={iso}
              onClick={() => setSelected(iso)}
              onDoubleClick={() => setEditing({ day: iso })}
              className={cx(
                'flex flex-col gap-1 overflow-hidden border-r border-b border-border-subtle p-1.5 text-left transition-colors',
                outside && 'opacity-40',
                selected === iso ? 'bg-[var(--color-accent-fade)]' : 'hover:bg-surface-high/40',
              )}
            >
              <span
                className={cx(
                  'tabular flex size-6 shrink-0 items-center justify-center rounded-full font-mono text-[12px]',
                  isToday
                    ? 'bg-accent font-medium text-[color:var(--color-on-accent)]'
                    : 'text-muted',
                )}
              >
                {day.day}
              </span>

              {dayEvents.slice(0, 3).map((ev) => (
                <span
                  key={ev.instanceId}
                  className="flex w-full items-center gap-1 truncate rounded-sm border-l-2 py-0.5 pl-1.5 text-[12px] leading-tight"
                  style={{
                    borderLeftColor: colorOf(ev),
                    background: `color-mix(in srgb, ${colorOf(ev)} 10%, transparent)`,
                    color: colorOf(ev),
                  }}
                >
                  {iconOf(ev) && <Icon name={iconOf(ev)!} size={11} className="shrink-0" />}
                  <span className="truncate">{ev.title}</span>
                </span>
              ))}
              {dayEvents.length > 3 && (
                <span className="tabular pl-1 font-mono text-[11px] text-muted">
                  +{dayEvents.length - 3} more
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selected && (
        <DayPanel
          day={selected}
          zone={zone}
          events={selectedEvents}
          colorOf={colorOf}
          onClose={() => setSelected(null)}
          onAdd={() => setEditing({ day: selected })}
          onPick={(instance) => setEditing({ instance })}
        />
      )}

      {editing && (
        <EventEditor
          instance={editing.instance}
          defaultDay={editing.day}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function DayPanel({
  day,
  zone,
  events,
  colorOf,
  onClose,
  onAdd,
  onPick,
}: {
  day: string;
  zone: string;
  events: EventInstance[];
  colorOf: (e: EventInstance) => string;
  onClose: () => void;
  onAdd: () => void;
  onPick: (e: EventInstance) => void;
}) {
  const d = DateTime.fromISO(day, { zone });

  return (
    <div className="max-h-[42%] shrink-0 overflow-y-auto border-t border-border bg-surface">
      <div className="sticky top-0 flex items-center gap-3 border-b border-border-subtle bg-surface px-4 py-3 md:px-6">
        <div>
          <span className="eyebrow">{d.toFormat('cccc')}</span>
          <h2 className="font-display text-[18px] leading-tight text-primary">
            {d.toFormat('LLLL d')}
          </h2>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="secondary" size="sm" icon="plus" onClick={onAdd}>
            Add
          </Button>
          <IconButton icon="close" label="Close day" size="sm" onClick={onClose} />
        </div>
      </div>

      <div className="flex flex-col gap-1.5 px-4 py-3 md:px-6">
        {events.length === 0 && (
          <p className="py-6 text-center text-[13px] text-muted">Nothing scheduled.</p>
        )}
        {events.map((ev) => (
          <button
            key={ev.instanceId}
            onClick={() => onPick(ev)}
            className="group flex items-center gap-3 rounded-md border border-border-subtle border-l-2 bg-surface px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-surface-high/60"
            style={{ borderLeftColor: colorOf(ev) }}
          >
            <span className="tabular w-24 shrink-0 font-mono text-[12px] text-muted">
              {fmtRange(ev, zone)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] text-primary">{ev.title}</span>
              {(ev.location || ev.readOnly) && (
                <span className="block truncate font-mono text-[12px] text-muted">
                  {[ev.location, ev.readOnly ? ev.feedName : null].filter(Boolean).join(' · ')}
                </span>
              )}
            </span>
            {ev.isRecurring && <Icon name="repeat" size={12} className="shrink-0 text-muted" />}
            {ev.source === 'agent' && <Badge tone="info">Exponent</Badge>}
            {ev.readOnly && <Badge>Feed</Badge>}
          </button>
        ))}
      </div>
    </div>
  );
}
